<template>
  <div class="registration-layout">
    <registration-header @close="onCloseClicked" :showCloseButton="ableToClose" :title="$t('registration.sp.company')"/>
    <div class="content-area">
      <router-view />
    </div>
    <registration-stepper
      :currentStep="spCompanyCurrentStep"
      :filledStepsCnt="spCompanyFilledStepsCnt"
      :visitedStepsCnt="spCompanyVisitedStepsCnt"
      :stepNames="stepNames"
      :loadingNext="loadingNext"
      :allowNext="allowNext"
      @clicked="onChangeStep"
    />
    <yes-no-popup
      :visible-popup="showCloseConfirmationPopup"
      title="registration.close.title"
      message="registration.close.message"
      yesCaption="confirm.ok"
      noCaption="confirm.cancel"
      @clicked="onClickYesNoButton"
    />
    <error-message :errorText="errorMessage" @close="errorMessage = ''" />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

import { views, SP_COMPANY_REGISTRATION_ROUTE_MAP } from '@/router/constants';

import YesNoPopup from '@/components/popup/YesNoPopup.vue';
import ErrorMessage from '@/components/common/ErrorMessage.vue';
import RegistrationHeader from '@/components/registration/RegistrationHeader.vue';
import RegistrationStepper from '@/components/registration/RegistrationStepper.vue';
import { loadRecaptcha } from '@/utils/recaptcha-loader';
import {
  CHANGE_SP_COMPANY_REGISTRATION_CURRENT_STEP,
  CHANGE_SP_COMPANY_REGISTRATION_FILLED_STEPS_COUNT, CHANGE_SP_COMPANY_REGISTRATION_RECAPTCHA_TOKEN,
  CHANGE_SP_COMPANY_REGISTRATION_VISITED_STEPS_COUNT, CLEAN_SP_COMPANY_REGISTRATION_DATA,
  LOAD_SP_COMPANY_CERTIFICATES,
  LOAD_SP_COMPANY_EMPLOYEES_AMOUNT,
  LOAD_SP_COMPANY_REGIONS,
  LOAD_SP_COMPANY_SERVICES, REGISTER_SP_COMPANY,
} from '@/store/spCompanyRegistration/actionTypes';

export default {
  name: 'SPCompanyRegistrationLayout',
  components: {
    YesNoPopup,
    ErrorMessage,
    RegistrationHeader,
    RegistrationStepper,
  },
  data() {
    return {
      showCloseConfirmationPopup: false,
      errorMessage: '',
      loadingNext: false,
    };
  },
  async mounted() {
    // Load reCAPTCHA dynamically only for registration
    await loadRecaptcha();

    await this.$store.dispatch(LOAD_SP_COMPANY_SERVICES);
    await this.$store.dispatch(LOAD_SP_COMPANY_CERTIFICATES);
    await this.$store.dispatch(LOAD_SP_COMPANY_REGIONS);
    await this.$store.dispatch(LOAD_SP_COMPANY_EMPLOYEES_AMOUNT);
  },
  computed: {
    stepNames() {
      return [
        'registration.sp.company.info',
        'registration.sp.company.services',
        'registration.sp.company.certifications',
        'registration.sp.company.email.verification',
        'registration.sp.company.registration.finish',
      ];
    },
    ...mapGetters([
      'spCompanyCurrentStep',
      'allowNextFirstStepSPCompany',
      'allowNextSecondStepSPCompany',
      'allowNextThirdStepSPCompany',
      'allowNextForthStepSPCompany',
      'spCompanyRegions',
      'spCompanyServices',
      'spCompanyCertificates',
      'spCompanyVisitedStepsCnt',
      'spCompanyFilledStepsCnt',
    ]),
    allowNext() {
      if (this.spCompanyCurrentStep === 1) {
        return this.allowNextFirstStepSPCompany;
      }
      if (this.spCompanyCurrentStep === 2) {
        return this.allowNextSecondStepSPCompany;
      }
      if (this.spCompanyCurrentStep === 3) {
        return this.allowNextThirdStepSPCompany;
      }
      if (this.spCompanyCurrentStep === 4) {
        return this.allowNextForthStepSPCompany;
      }
      return true;
    },
    ableToClose() {
      return this.spCompanyCurrentStep !== 5;
    },
  },
  methods: {
    async getRecaptchaToken() {
      await this.$recaptchaLoaded();
      this.recaptchaUserToken = await this.$recaptcha('login');
      this.$recaptchaInstance.showBadge();

      if (this.recaptchaUserToken) {
        await this.$store.dispatch(CHANGE_SP_COMPANY_REGISTRATION_RECAPTCHA_TOKEN, this.recaptchaUserToken);
      } else {
        this.errorText = this.$t('registration.client.info.recaptcha.is.empty.or.not.valid');
      }
    },

    async onChangeStep(stepNumber) {
      if (stepNumber === 2) {
        await this.getRecaptchaToken();
      }
      if (stepNumber === 5) {
        this.loadingNext = true;
        await this.getRecaptchaToken();
        try {
          await this.$store.dispatch(REGISTER_SP_COMPANY);
        } catch (e) {
          this.errorMessage = e.message;
          throw e;
        } finally {
          this.loadingNext = false;
        }
      }

      await this.$store.dispatch(CHANGE_SP_COMPANY_REGISTRATION_CURRENT_STEP, Number(stepNumber));
      await this.$store.dispatch(CHANGE_SP_COMPANY_REGISTRATION_FILLED_STEPS_COUNT, 1);
      if (stepNumber > this.spCompanyVisitedStepsCnt) {
        await this.$store.dispatch(CHANGE_SP_COMPANY_REGISTRATION_VISITED_STEPS_COUNT, Number(stepNumber));
      }
    },
    onCloseClicked() {
      this.showCloseConfirmationPopup = true;
    },
    async onClickYesNoButton(result) {
      this.showCloseConfirmationPopup = false;

      if (result) {
        await this.$store.dispatch(CLEAN_SP_COMPANY_REGISTRATION_DATA);
        await this.$router.replace({ name: views.SIGN_IN });
      }
    },
  },
  watch: {
    spCompanyCurrentStep: {
      handler() {
        this.$router.replace(SP_COMPANY_REGISTRATION_ROUTE_MAP[this.spCompanyCurrentStep]);
      },
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~@/assets/styles/design-system';

.registration-layout {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  flex: 1;
  background-color: var(--bg-primary);
}

.content-area {
  margin-bottom: rem(72);
  padding: 0 rem(192);
  display: flex;
  flex-direction: row;
  justify-content: center;
  padding-top: rem(30);
}

.registration-stepper {
  position: fixed;
  z-index: 1;
  background-color: $white;
  bottom: 0;
  width: 100%;
  padding: rem(10) rem(116);
  box-shadow: $shadow-md;
}
</style>
